import { useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { ChevronDown, Info, Search } from 'lucide-react';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Card } from '@/components/ui/card';
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableMessageRow,
  TablePagination,
  TableRow,
  TableScroller,
  TableSkeletonRows,
  TableSortHead,
} from '@/components/ui/table';
import { messages } from '@/i18n';
import { useApi } from '@/api/apiContext';
import {
  customerShipmentsQueryOptions,
  groundHandlerShipmentsQueryOptions,
  shipmentDeviationsQueryOptions,
  shipmentsQueryOptions,
  subscriptionsQueryOptions,
  type ShipmentScope,
  type ShipmentSortBy,
  type SortDir,
} from '@/api/endpoints/shipments';
import {
  SHIPMENT_STATUSES,
  type CustomerShipmentRow,
  type GroundHandlerShipmentRow,
  type ShipmentRow,
  type ShipmentStatus,
} from '@/types/shipment';
import { formatLocalDateTime } from '@/lib/datetime';
import { shipmentStatusBadgeClass } from './shipmentsData';

const PAGE_SIZE = 25;
const SEARCH_DEBOUNCE_MS = 300;

interface SortState {
  sortBy: ShipmentSortBy;
  sortDir: SortDir;
}

const DEFAULT_SORT: SortState = { sortBy: 'eta', sortDir: 'desc' };

/** The Delivered tab's fixed status filter — module-level so the params memo below sees a stable reference. */
const DELIVERED_STATUSES: ShipmentStatus[] = ['PalletScanned'];

function SortableHeader({
  label,
  sortKey,
  sort,
  onSort,
}: {
  label: string;
  sortKey: ShipmentSortBy;
  sort: SortState;
  onSort: (key: ShipmentSortBy) => void;
}) {
  return (
    <TableSortHead
      label={label}
      active={sort.sortBy === sortKey}
      direction={sort.sortDir}
      onSort={() => onSort(sortKey)}
    />
  );
}

export function ShipmentListScreen({
  scope = 'tollit',
  variant = 'active',
}: {
  scope?: ShipmentScope;
  /**
   * Tollit scope only. 'delivered' filters the list to PalletScanned shipments
   * — the Delivered admin tab. 'deviations' reads GET /api/v1/shipments/deviations
   * instead — the Deviations admin tab: same row shape, filters and sorting,
   * narrowed server-side to shipments whose submitted order count deviates
   * from the parcel count.
   */
  variant?: 'active' | 'delivered' | 'deviations';
}) {
  const t = messages.shipmentList;
  const statusLabels = messages.shipmentStatus;
  const api = useApi();
  const showCustomerFilter = scope === 'tollit';
  // The Delivered tab is itself a status filter (PalletScanned only), so a
  // user-chosen status filter — or sorting by a status every row shares —
  // would only contradict or no-op against it.
  const showStatusFilter = variant !== 'delivered';
  const showCustomerColumn = scope !== 'customer';
  // submittedOrder ("API Received") is on every scope's row shape (ShipmentRow,
  // GroundHandlerShipmentRow, CustomerShipmentRow all carry it) — only the
  // Customer column and dropdown vary by scope.
  // One source for the column count, so the loading/empty rows' colSpan can
  // never drift from the header actually rendered below.
  const columnKeys = [
    'no',
    'status',
    ...(showCustomerColumn ? ['customer'] : []),
    'eta',
    'awb',
    'boxes',
    'palletScanned',
    'weight',
    'parcels',
    'submitted',
    'noa',
    'noaQuantity',
    'customsCleared',
    'driverCollection',
  ];
  const columnCount = columnKeys.length;
  const title =
    variant === 'delivered'
      ? t.titleDelivered
      : variant === 'deviations'
        ? t.titleDeviations
        : t.title;
  const subtitle =
    variant === 'delivered'
      ? t.subtitleDelivered
      : variant === 'deviations'
        ? t.subtitleDeviations
        : t.subtitle;

  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [customerFilter, setCustomerFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState<ShipmentStatus[]>([]);
  const [etaFrom, setEtaFrom] = useState('');
  const [etaTo, setEtaTo] = useState('');
  const [sort, setSort] = useState<SortState>(DEFAULT_SORT);
  const [skip, setSkip] = useState(0);

  // Debounce free text into a separate value: firing a query on every
  // keystroke would spam the endpoint, and unlike the plain select/date
  // filters below, typing produces many changes in quick succession. Resetting
  // the page here, once the debounced value actually changes, rather than in
  // the onChange above keeps the page from jumping back on every keystroke.
  // Skipped on mount: the first run debounces an empty box into the empty
  // search it already holds, and the setSkip(0) riding along with it lands
  // 300ms after the screen appears — long enough for someone who paged
  // forward immediately to be bounced back to page one by a filter they never
  // touched.
  const searchSettled = useRef(false);
  useEffect(() => {
    if (!searchSettled.current) {
      searchSettled.current = true;
      return;
    }
    const timer = window.setTimeout(() => {
      setSearch(searchInput.trim());
      setSkip(0);
    }, SEARCH_DEBOUNCE_MS);
    return () => window.clearTimeout(timer);
  }, [searchInput]);

  const subscriptionsQuery = useQuery({
    ...subscriptionsQueryOptions(api),
    enabled: showCustomerFilter,
  });
  const subscriptions = subscriptionsQuery.data ?? [];

  const params = useMemo(
    () => ({
      skip,
      take: PAGE_SIZE,
      search: search || undefined,
      subscriptionId: showCustomerFilter && customerFilter !== 'all' ? [customerFilter] : undefined,
      etaFrom: etaFrom || undefined,
      etaTo: etaTo || undefined,
      sortBy: sort.sortBy,
      sortDir: sort.sortDir,
      status: showStatusFilter
        ? statusFilter.length
          ? statusFilter
          : undefined
        : DELIVERED_STATUSES,
    }),
    [
      skip,
      search,
      showCustomerFilter,
      customerFilter,
      etaFrom,
      etaTo,
      sort,
      showStatusFilter,
      statusFilter,
    ],
  );

  // One hook per source, not one hook fed a unioned `queryOptions` — each scope is a
  // structurally different row (no customerName/subscriptionId on the
  // customer row, no subscriptionId on the ground handler row — see
  // CustomerShipmentRow / GroundHandlerShipmentRow), and a single `useQuery`
  // call collapses `.data` to `unknown` under TanStack Query's generics (same
  // reasoning as ShipmentDetailScreen's three-hook split). `enabled` keeps
  // only the query matching `scope` actually fetching, and calling all of them
  // keeps this a fixed number of hooks per render regardless of `scope`.
  // The Deviations tab is the same Tollit row from a different endpoint — its
  // own hook rather than a ternary over the two options objects, whose
  // differently-shaped query keys don't unify under `useQuery`'s overloads.
  const tollitListQuery = useQuery({
    ...shipmentsQueryOptions(api, params),
    enabled: scope === 'tollit' && variant !== 'deviations',
    placeholderData: keepPreviousData,
  });
  const tollitDeviationsQuery = useQuery({
    ...shipmentDeviationsQueryOptions(api, params),
    enabled: scope === 'tollit' && variant === 'deviations',
    placeholderData: keepPreviousData,
  });
  const tollitQuery = variant === 'deviations' ? tollitDeviationsQuery : tollitListQuery;
  const handlerQuery = useQuery({
    ...groundHandlerShipmentsQueryOptions(api, params),
    enabled: scope === 'handler',
    placeholderData: keepPreviousData,
  });
  const customerQuery = useQuery({
    ...customerShipmentsQueryOptions(api, params),
    enabled: scope === 'customer',
    placeholderData: keepPreviousData,
  });
  const shipmentsQuery =
    scope === 'handler' ? handlerQuery : scope === 'customer' ? customerQuery : tollitQuery;
  // Explicitly annotated: read through the unioned query above, TanStack
  // Query's generics collapse `.data` to `unknown` instead of the
  // `ShipmentRow[] | GroundHandlerShipmentRow[] | CustomerShipmentRow[]` union.
  const items: (ShipmentRow | GroundHandlerShipmentRow | CustomerShipmentRow)[] =
    (scope === 'handler'
      ? handlerQuery.data?.items
      : scope === 'customer'
        ? customerQuery.data?.items
        : tollitQuery.data?.items) ?? [];
  const total =
    (scope === 'handler'
      ? handlerQuery.data?.total
      : scope === 'customer'
        ? customerQuery.data?.total
        : tollitQuery.data?.total) ?? 0;
  const rangeStart = total === 0 ? 0 : skip + 1;
  const rangeEnd = Math.min(skip + PAGE_SIZE, total);

  function toggleSort(key: ShipmentSortBy) {
    setSort((prev) =>
      prev.sortBy === key
        ? { sortBy: key, sortDir: prev.sortDir === 'asc' ? 'desc' : 'asc' }
        : { sortBy: key, sortDir: 'desc' },
    );
    setSkip(0);
  }

  // Kept in SHIPMENT_STATUSES order whatever order they were ticked in, so the
  // same selection always produces the same `status` CSV and query key.
  function toggleStatus(status: ShipmentStatus, checked: boolean) {
    setStatusFilter((prev) =>
      SHIPMENT_STATUSES.filter((s) => (s === status ? checked : prev.includes(s))),
    );
    setSkip(0);
  }

  const statusFilterLabel =
    statusFilter.length > 1
      ? t.statusFilterCount.replace('{count}', String(statusFilter.length))
      : statusFilter[0]
        ? statusLabels[statusFilter[0]]
        : t.statusFilterAll;

  return (
    <section>
      <div className="mb-4 flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">{title}</h1>
          <p className="text-muted-foreground mt-1 max-w-prose text-sm">{subtitle}</p>
        </div>
      </div>

      <Card>
        <div className="border-border/60 flex flex-wrap items-center gap-2 border-b p-4">
          <div className="border-border bg-secondary/40 flex min-w-[220px] flex-1 items-center gap-2 rounded-md border px-3 py-2 sm:max-w-xs">
            <Search className="text-muted-foreground size-4 shrink-0" />
            <input
              className="text-foreground placeholder:text-muted-foreground w-full bg-transparent text-sm outline-none"
              placeholder={scope === 'customer' ? t.searchPlaceholderCustomer : t.searchPlaceholder}
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
            />
          </div>
          {showCustomerFilter ? (
            <select
              className="border-border bg-secondary/40 h-9 rounded-md border px-3 text-sm outline-none"
              value={customerFilter}
              onChange={(e) => {
                setCustomerFilter(e.target.value);
                setSkip(0);
              }}
            >
              <option value="all">{t.customerFilterAll}</option>
              {subscriptions.map((s) => (
                <option key={s.subscriptionId} value={s.subscriptionId}>
                  {s.name}
                </option>
              ))}
            </select>
          ) : null}
          {showStatusFilter ? (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button
                  type="button"
                  className="border-border bg-secondary/40 flex h-9 items-center gap-2 rounded-md border px-3 text-sm outline-none"
                >
                  {statusFilterLabel}
                  <ChevronDown className="text-muted-foreground size-4 shrink-0" aria-hidden />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="start">
                {SHIPMENT_STATUSES.map((status) => (
                  <DropdownMenuCheckboxItem
                    key={status}
                    checked={statusFilter.includes(status)}
                    onCheckedChange={(checked) => toggleStatus(status, checked)}
                    // Keep the menu open so several statuses can be ticked in one go.
                    onSelect={(e) => e.preventDefault()}
                  >
                    {statusLabels[status]}
                  </DropdownMenuCheckboxItem>
                ))}
                {statusFilter.length ? (
                  <>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem
                      onSelect={() => {
                        setStatusFilter([]);
                        setSkip(0);
                      }}
                    >
                      {t.statusFilterClear}
                    </DropdownMenuItem>
                  </>
                ) : null}
              </DropdownMenuContent>
            </DropdownMenu>
          ) : null}
          <label className="text-muted-foreground flex items-center gap-1.5 text-xs">
            {t.dateFrom}
            <input
              type="date"
              className="border-border bg-secondary/40 text-foreground h-9 rounded-md border px-2 text-sm outline-none"
              value={etaFrom}
              onChange={(e) => {
                setEtaFrom(e.target.value);
                setSkip(0);
              }}
            />
          </label>
          <label className="text-muted-foreground flex items-center gap-1.5 text-xs">
            {t.dateTo}
            <input
              type="date"
              className="border-border bg-secondary/40 text-foreground h-9 rounded-md border px-2 text-sm outline-none"
              value={etaTo}
              onChange={(e) => {
                setEtaTo(e.target.value);
                setSkip(0);
              }}
            />
          </label>
        </div>

        {shipmentsQuery.isError ? (
          <Alert variant="destructive" className="m-4">
            <Info aria-hidden />
            <AlertDescription>{t.loadError}</AlertDescription>
          </Alert>
        ) : (
          <TableScroller label={title} boundHeight>
            <Table columnCount={columnCount} className="min-w-[1180px]">
              <TableHeader sticky>
                <tr>
                  <TableHead className="w-12">{t.columnNo}</TableHead>
                  {showStatusFilter ? (
                    <SortableHeader
                      label={t.columnStatus}
                      sortKey="status"
                      sort={sort}
                      onSort={toggleSort}
                    />
                  ) : (
                    <TableHead>{t.columnStatus}</TableHead>
                  )}
                  {showCustomerColumn ? <TableHead>{t.columnCustomer}</TableHead> : null}
                  <SortableHeader
                    label={t.columnEta}
                    sortKey="eta"
                    sort={sort}
                    onSort={toggleSort}
                  />
                  <TableHead>{t.columnAwb}</TableHead>
                  <TableHead align="right">{t.columnPallets}</TableHead>
                  <TableHead align="right">{t.columnPalletScanned}</TableHead>
                  <TableHead align="right">{t.columnWeight}</TableHead>
                  <TableHead align="right">{t.columnParcels}</TableHead>
                  <TableHead align="right">{t.columnSubmitted}</TableHead>
                  <SortableHeader
                    label={t.columnNoa}
                    sortKey="noa"
                    sort={sort}
                    onSort={toggleSort}
                  />
                  <TableHead align="right">{t.columnNoaQuantity}</TableHead>
                  <SortableHeader
                    label={t.columnCustomsCleared}
                    sortKey="customsClearedAt"
                    sort={sort}
                    onSort={toggleSort}
                  />
                  <TableHead align="right">{t.columnDriverCollection}</TableHead>
                </tr>
              </TableHeader>
              <TableBody>
                {shipmentsQuery.isLoading ? (
                  <TableSkeletonRows rows={10} />
                ) : (
                  items.map((row, index) => (
                    <TableRow key={row.transportDocumentNo}>
                      <TableCell muted className="tabular-nums">
                        {skip + index + 1}
                      </TableCell>
                      <TableCell>
                        <Badge className={shipmentStatusBadgeClass(row.status)}>
                          {statusLabels[row.status as ShipmentStatus] ?? row.status}
                        </Badge>
                      </TableCell>
                      {showCustomerColumn && 'customerName' in row ? (
                        <TableCell muted className="max-w-52 truncate">
                          {row.customerName ?? '—'}
                        </TableCell>
                      ) : null}
                      <TableCell muted className="whitespace-nowrap tabular-nums">
                        {formatLocalDateTime(row.eta)}
                      </TableCell>
                      <TableCell>
                        {/* The row's identity, and the only link out of it:
                            worth reading as one token rather than wrapping
                            mid-number, and worth the mono figures. */}
                        <Link
                          to={row.transportDocumentNo}
                          className="rounded-sm font-medium whitespace-nowrap tabular-nums hover:underline"
                        >
                          {row.transportDocumentNo}
                        </Link>
                      </TableCell>
                      <TableCell muted numeric>
                        {row.totalNoOfBoxes}
                      </TableCell>
                      <TableCell muted numeric>
                        {row.palletScannedOrder ?? '—'}
                      </TableCell>
                      <TableCell muted numeric>
                        {row.totalNetWeight}
                      </TableCell>
                      <TableCell muted numeric>
                        {row.noOfParcels}
                      </TableCell>
                      <TableCell muted numeric>
                        {row.submittedOrder}
                      </TableCell>
                      <TableCell className="whitespace-nowrap tabular-nums">
                        {row.noaDateTime ? (
                          <span className="text-muted-foreground">
                            {formatLocalDateTime(row.noaDateTime)}
                            {row.noaCount > 1 ? ` ×${row.noaCount}` : ''}
                          </span>
                        ) : (
                          <Badge variant="outline">{t.noaPending}</Badge>
                        )}
                      </TableCell>
                      <TableCell numeric muted>
                        {row.noaQuantity ?? '—'} / {row.totalNoOfBoxes}
                      </TableCell>
                      <TableCell muted className="whitespace-nowrap tabular-nums">
                        {row.customsClearedAt ? formatLocalDateTime(row.customsClearedAt) : '—'}
                      </TableCell>
                      <TableCell muted numeric>
                        {row.driverCollected ?? '—'}
                      </TableCell>
                    </TableRow>
                  ))
                )}
                {!shipmentsQuery.isLoading && items.length === 0 ? (
                  <TableMessageRow>{t.emptyState}</TableMessageRow>
                ) : null}
              </TableBody>
            </Table>
          </TableScroller>
        )}

        <TablePagination
          summary={
            shipmentsQuery.isLoading
              ? t.loading
              : `${total === 0 ? '0' : `${rangeStart}–${rangeEnd}`} / ${total}`
          }
          previousLabel={t.previousPage}
          nextLabel={t.nextPage}
          canGoBack={skip > 0}
          canGoForward={rangeEnd < total}
          onPrevious={() => setSkip(Math.max(0, skip - PAGE_SIZE))}
          onNext={() => setSkip(skip + PAGE_SIZE)}
        />
      </Card>
    </section>
  );
}
